import VersusPage, {
  type VersusComparisonRow,
  type VersusFaq,
  type VersusStat,
  type VersusWorkflow,
} from "@/components/public/versus-page"
import type { SeoMeta } from "@/types"

const STATS: VersusStat[] = [
  {
    value: "0",
    label: "site visits needed",
    detail: "QuiteRight builds from portal photos. No camera, no tripod, no travel.",
  },
  {
    value: "60s",
    label: "listing to ad",
    detail: "From pasted URL to a published video ad \u2014 not a virtual tour, a marketing asset.",
  },
  {
    value: "4",
    label: "social channels",
    detail: "One video auto-published to Instagram, Facebook, TikTok and YouTube.",
  },
  {
    value: "\u00A3499",
    label: "all-in per month",
    detail: "Fully managed, unlimited videos. No per-listing camera rental, no per-tour fee.",
  },
]

const WORKFLOW: VersusWorkflow = {
  competitorSteps: [
    "Book a site visit",
    "Scan each room with the Giraffe360 camera",
    "Upload scans and wait for processing",
    "Review the virtual tour, fix any bad scans",
    "Embed on your website / share on Rightmove",
    "Still need a social video? Different tool entirely",
  ],
  competitorTotal: "Half-day visit + processing",
  quiterightSteps: [
    "Paste the listing URL",
    "QuiteRight builds the video and captions automatically",
    "Publish to every social channel in one click",
  ],
  quiterightTotal: "60 seconds",
}

const COMPARISON: VersusComparisonRow[] = [
  { label: "Primary output", competitor: "3D virtual tour + HDR stills", quiteright: "Short-form social video ads", highlight: true },
  { label: "Where it lives", competitor: "Embedded on listing page", quiteright: "Instagram, TikTok, Facebook, YouTube", highlight: true },
  { label: "Requires site visit", competitor: "Yes \u2014 someone scans the property", quiteright: "No \u2014 builds from portal photos", highlight: true },
  { label: "Time to first asset", competitor: "Half-day + processing", quiteright: "Under 60 seconds" },
  { label: "Voiceover and script", competitor: "Not included", quiteright: "AI voiceover + UK property script" },
  { label: "Listings covered", competitor: "Premium only \u2014 too slow for every listing", quiteright: "Every listing gets a video", highlight: true },
  { label: "Branding", competitor: "Tour viewer branding", quiteright: "Your logo and colours on every frame" },
  { label: "Pricing", competitor: "Per-visit or subscription camera", quiteright: "\u00A3499/month, unlimited video" },
  { label: "Best use case", competitor: "Flagship high-value listings", quiteright: "Every listing, every week" },
]

const FAQ: VersusFaq[] = [
  {
    q: "Is QuiteRight a virtual tour replacement?",
    a: "No. Giraffe360 produces immersive 3D walkthroughs that live on your listing page. QuiteRight produces social-first video ads that live on Reels, TikTok and Shorts. They do different jobs \u2014 one demonstrates the property to serious buyers, the other gets the phone to stop scrolling.",
  },
  {
    q: "Should I use both?",
    a: "Yes, if budget allows. Use Giraffe360 for flagship listings that justify a site visit and 3D capture. Use QuiteRight to give every listing a proper social video ad, without booking site visits. Most top-performing agencies pair a premium capture product with a scalable video ad pipeline.",
  },
  {
    q: "Can QuiteRight use Giraffe360 photos?",
    a: "Yes. Giraffe360's HDR stills are gorgeous \u2014 once they're live on Rightmove or Zoopla, paste the URL and QuiteRight will use them. Or upload the stills directly into the QuiteRight listing.",
  },
  {
    q: "Which is better ROI for a small agency?",
    a: "For a small agency with 10\u201330 listings live, QuiteRight usually wins on ROI: every listing gets a social video for a flat \u00A3499/month, with zero site visits. Giraffe360 makes sense when you consistently list million-pound-plus properties where a 3D tour moves the needle.",
  },
  {
    q: "Does QuiteRight produce 3D floorplans?",
    a: "No. QuiteRight is focused on short-form video ads for social channels. For 3D floorplans and virtual walkthroughs, Giraffe360 or Matterport remain the right tools. Pair them with QuiteRight for the video marketing layer.",
  },
]

export default function VsGiraffe360({ meta }: { meta: SeoMeta }) {
  return (
    <VersusPage
      meta={meta}
      competitorName="Giraffe360"
      breadcrumbName="QuiteRight vs Giraffe360"
      heroHeadline="Virtual tours vs social video ads. Both have a place"
      heroSubhead="Giraffe360 captures beautiful 3D tours and HDR stills \u2014 on-site, per listing. QuiteRight turns the listing you already have into a social video ad in 60 seconds. Different jobs, and most agencies benefit from both."
      statsHeadline="Scale every listing, not just the flagships"
      statsSubhead="Site visits don't scale. Portal photos do. QuiteRight covers every listing, every week, without sending anyone out with a camera."
      stats={STATS}
      workflowHeadline="One listing, two workflows"
      workflow={WORKFLOW}
      comparison={COMPARISON}
      faq={FAQ}
      ctaHeadline="Virtual tour the flagships. Video-ad every listing"
      ctaBody="Use Giraffe360 where a 3D tour justifies the site visit. Use QuiteRight to give every other listing the social video it deserves."
      relatedKey="vs/giraffe360"
    />
  )
}
